<template>
  <div class="damages-container">
    <!-- Header -->
    <div class="damages-header">
      <h4 class="damages-title mb-0">Afectaciones</h4>

      <div class="damages-actions">
        <!-- City selector -->
        <v-select
          v-model="selectedCity"
          :options="cityOptions"
          :reduce="(opt) => opt.value"
          label="label"
          :placeholder="$t('city') || 'Ciudad'"
          class="city-selector"
          :clearable="false"
        />

        <!-- Filters button -->
        <b-button-group>
          <b-button
            class="buttonFilter cursor-pointer d-flex align-items-center"
            @click="showFilterModal = true"
          >
            <b-icon-filter-circle-fill scale="1.1" />
            <span class="align-middle ml-1 text-capitalize">
              {{ $t("filters") }}
            </span>
          </b-button>
          <b-button
            class="buttonFilter"
            @click="clearFilters"
            @mouseover="showTrashIcon = true"
            @mouseleave="showTrashIcon = false"
          >
            <span class="d-flex align-items-center justify-content-center">
              <feather-icon icon="XIcon" v-if="showTrashIcon" />
              <span v-else>{{ filtersCount }}</span>
            </span>
          </b-button>
        </b-button-group>

        <!-- Download button -->
        <b-button
          variant="primary"
          size="sm"
          class="btn-icon"
          @click="exportData"
          :disabled="exporting"
          v-b-tooltip.hover
          title="Descargar"
        >
          <feather-icon icon="DownloadCloudIcon" size="15" />
        </b-button>
      </div>
    </div>

    <!-- Filter modal -->
    <b-modal
      id="crisis-damages-filter-modal"
      v-model="showFilterModal"
      centered
      hide-header
      no-close-on-esc
      no-close-on-backdrop
      scrollable
      size="lg"
    >
      <p class="h3 text-capitalize filterTittle">{{ $t("messages.info.advanced_search") || 'Búsqueda avanzada' }}</p>
      <hr />

      <div class="filters-grid">
        <b-form-group label="Estado">
          <v-select
            v-model="filterState"
            :options="stateOptions"
            :reduce="(opt) => opt.value"
            label="label"
            placeholder="Seleccionar estado"
            multiple
          />
        </b-form-group>

        <b-form-group label="Municipio">
          <v-select
            v-model="filterMunicipality"
            :options="filteredMunicipalityOptions"
            :reduce="(opt) => opt.value"
            label="label"
            placeholder="Seleccionar municipio"
            multiple
          />
        </b-form-group>
      </div>

      <template #modal-footer>
        <b-button variant="primary" size="sm" @click="clearFilters" class="text-capitalize">
          {{ $t("messages.info.clear_filters") || 'Limpiar filtros' }}
        </b-button>
        <b-button variant="primary" size="sm" @click="showFilterModal = false" class="text-capitalize">
          {{ $t("close") || 'Cerrar' }}
        </b-button>
        <b-button variant="primary" size="sm" @click="applyFilters" class="text-capitalize">
          {{ $t("search") || 'Buscar' }}
          &nbsp;
          <feather-icon icon="SearchIcon" />
        </b-button>
      </template>
    </b-modal>

    <!-- Scrollable table wrapper -->
    <div class="damages-table-wrapper">
      <table class="damages-table">
        <thead>
          <!-- Municipality group headers -->
          <tr class="group-header-row">
            <th class="row-label-header sticky-col"></th>
            <th
              v-for="municipality in visibleMunicipalities"
              :key="'group-' + municipality.value"
              colspan="2"
              class="group-header"
            >
              {{ municipality.label }}
            </th>
            <th class="total-header" rowspan="2">Total</th>
          </tr>
          <!-- Sub-headers (Total / Riesgo) -->
          <tr class="sub-header-row">
            <th class="row-label-header sticky-col"></th>
            <th
              v-for="col in flatColumns"
              :key="col.key"
              class="sub-header"
            >
              {{ col.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in rowDefinitions" :key="row.key" class="data-row">
            <td class="row-label sticky-col">{{ row.label }}</td>
            <td
              v-for="col in flatColumns"
              :key="row.key + '-' + col.key"
              class="data-cell"
            >
              {{ getCellValue(row.key, col.municipalityId, col.field) }}
            </td>
            <td class="data-cell total-cell">
              {{ getRowTotal(row.key) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import { ref, computed } from "@vue/composition-api";
import vSelect from "vue-select";
import { VBTooltip } from "bootstrap-vue";
import { useFullscreenModalTransport } from "./components/composables/useFullscreenModalTransport";

export default {
  name: "Damages",
  components: {
    vSelect,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  props: {
    showFilters: {
      type: Boolean,
      default: true,
    },
  },
  setup() {
    // ── Filter state ───────────────────────────────────────
    const showFilterModal = ref(false);
    const showTrashIcon = ref(false);
    const exporting = ref(false);

    const filterState = ref([]);
    const filterMunicipality = ref([]);
    const appliedFilterState = ref([]);
    const appliedFilterMunicipality = ref([]);

    useFullscreenModalTransport('crisis-damages-filter-modal', showFilterModal);

    // ── Options ────────────────────────────────────────────
    const stateOptions = ref([
      { value: 1, label: "Oaxaca" },
      { value: 2, label: "Guerrero" },
      { value: 3, label: "Chiapas" },
      { value: 4, label: "Puebla" },
      { value: 5, label: "Veracruz" },
    ]);

    const cityOptions = ref([
      { value: 1, label: "Oaxaca" },
      { value: 2, label: "Guerrero" },
      { value: 3, label: "Chiapas" },
      { value: 4, label: "Puebla" },
      { value: 5, label: "Veracruz" },
    ]);

    const selectedCity = ref(1);

    const municipalityOptions = ref([
      { value: 1, label: "Santo Domingo Tehuantepec", stateId: 1 },
      { value: 2, label: "Oaxaca de Juárez", stateId: 1 },
      { value: 3, label: "Huatulco", stateId: 1 },
      { value: 4, label: "Acapulco", stateId: 2 },
      { value: 5, label: "Zihuatanejo", stateId: 2 },
      { value: 6, label: "Tuxtla Gutiérrez", stateId: 3 },
      { value: 7, label: "Puebla de Zaragoza", stateId: 4 },
      { value: 8, label: "Xalapa", stateId: 5 },
    ]);

    const filteredMunicipalityOptions = computed(() => {
      if (!filterState.value || filterState.value.length === 0) {
        return municipalityOptions.value;
      }
      return municipalityOptions.value.filter((m) =>
        filterState.value.includes(m.stateId)
      );
    });

    // ── Row definitions ────────────────────────────────────
    const rowDefinitions = [
      { key: "preventive_closures", label: "Cierres preventivos" },
      { key: "branches_offline", label: "Sucursales offline" },
      { key: "water_leak_ceiling", label: "Sucursales con filtración de agua / caída de plafones" },
      { key: "broken_glass", label: "Rotura de cristales" },
      { key: "damaged_cameras", label: "Cámaras dañadas" },
      { key: "buildings_offline", label: "Edificios offline" },
    ];

    // ── Mock data ──────────────────────────────────────────
    const tableData = ref({
      1: {
        preventive_closures: { total: 0, risk: 0 },
        branches_offline: { total: 6, risk: 0 },
        water_leak_ceiling: { total: 23, risk: 0 },
        broken_glass: { total: 44, risk: 0 },
        damaged_cameras: { total: 56, risk: 0 },
        buildings_offline: { total: 65, risk: 0 },
      },
      2: {
        preventive_closures: { total: 61, risk: 25 },
        branches_offline: { total: 23, risk: 13 },
        water_leak_ceiling: { total: 55, risk: 14 },
        broken_glass: { total: 22, risk: 3 },
        damaged_cameras: { total: 30, risk: 19 },
        buildings_offline: { total: 59, risk: 6 },
      },
      3: {
        preventive_closures: { total: 10, risk: 2 },
        branches_offline: { total: 5, risk: 1 },
        water_leak_ceiling: { total: 8, risk: 2 },
        broken_glass: { total: 3, risk: 0 },
        damaged_cameras: { total: 7, risk: 1 },
        buildings_offline: { total: 4, risk: 0 },
      },
      4: {
        preventive_closures: { total: 25, risk: 5 },
        branches_offline: { total: 12, risk: 3 },
        water_leak_ceiling: { total: 15, risk: 4 },
        broken_glass: { total: 8, risk: 1 },
        damaged_cameras: { total: 18, risk: 3 },
        buildings_offline: { total: 10, risk: 2 },
      },
      5: {
        preventive_closures: { total: 14, risk: 3 },
        branches_offline: { total: 7, risk: 2 },
        water_leak_ceiling: { total: 9, risk: 1 },
        broken_glass: { total: 5, risk: 0 },
        damaged_cameras: { total: 11, risk: 2 },
        buildings_offline: { total: 6, risk: 1 },
      },
      6: {
        preventive_closures: { total: 30, risk: 6 },
        branches_offline: { total: 18, risk: 4 },
        water_leak_ceiling: { total: 20, risk: 5 },
        broken_glass: { total: 12, risk: 2 },
        damaged_cameras: { total: 22, risk: 4 },
        buildings_offline: { total: 15, risk: 3 },
      },
      7: {
        preventive_closures: { total: 35, risk: 8 },
        branches_offline: { total: 20, risk: 5 },
        water_leak_ceiling: { total: 28, risk: 6 },
        broken_glass: { total: 15, risk: 3 },
        damaged_cameras: { total: 25, risk: 5 },
        buildings_offline: { total: 18, risk: 4 },
      },
      8: {
        preventive_closures: { total: 20, risk: 4 },
        branches_offline: { total: 10, risk: 2 },
        water_leak_ceiling: { total: 12, risk: 3 },
        broken_glass: { total: 7, risk: 1 },
        damaged_cameras: { total: 14, risk: 3 },
        buildings_offline: { total: 9, risk: 2 },
      },
    });

    // ── Computed ────────────────────────────────────────────
    const visibleMunicipalities = computed(() => {
      let list = municipalityOptions.value;

      // Filter by selected city (state)
      if (selectedCity.value) {
        list = list.filter((m) => m.stateId === selectedCity.value);
      }

      // Apply advanced filters
      if (appliedFilterState.value.length > 0) {
        list = list.filter((m) => appliedFilterState.value.includes(m.stateId));
      }

      if (appliedFilterMunicipality.value.length > 0) {
        list = list.filter((m) => appliedFilterMunicipality.value.includes(m.value));
      }

      return list;
    });

    // Flat list of columns: [{ key, municipalityId, field, label }, ...]
    const flatColumns = computed(() => {
      const cols = [];
      visibleMunicipalities.value.forEach((m) => {
        cols.push({ key: `${m.value}-total`, municipalityId: m.value, field: 'total', label: 'Total' });
        cols.push({ key: `${m.value}-risk`, municipalityId: m.value, field: 'risk', label: 'Riesgo' });
      });
      return cols;
    });

    const filtersCount = computed(() => {
      let count = 0;
      if (appliedFilterState.value.length > 0) count++;
      if (appliedFilterMunicipality.value.length > 0) count++;
      return count;
    });

    // ── Methods ────────────────────────────────────────────
    const getCellValue = (rowKey, municipalityId, field) => {
      const municipalityData = tableData.value[municipalityId];
      if (!municipalityData || !municipalityData[rowKey]) return 0;
      return municipalityData[rowKey][field] || 0;
    };

    const getRowTotal = (rowKey) => {
      return visibleMunicipalities.value.reduce((sum, m) => {
        const data = tableData.value[m.value];
        if (!data || !data[rowKey]) return sum;
        return sum + (data[rowKey].total || 0);
      }, 0);
    };

    const applyFilters = () => {
      appliedFilterState.value = [...filterState.value];
      appliedFilterMunicipality.value = [...filterMunicipality.value];
      showFilterModal.value = false;
    };

    const clearFilters = () => {
      filterState.value = [];
      filterMunicipality.value = [];
      appliedFilterState.value = [];
      appliedFilterMunicipality.value = [];
    };

    const exportData = () => {
      exporting.value = true;
      // TODO: implement actual export logic
      setTimeout(() => {
        exporting.value = false;
      }, 1000);
    };

    return {
      // Filter
      showFilterModal,
      showTrashIcon,
      exporting,
      filterState,
      filterMunicipality,
      filtersCount,

      // Options
      stateOptions,
      cityOptions,
      selectedCity,
      municipalityOptions,
      filteredMunicipalityOptions,

      // Table
      rowDefinitions,
      visibleMunicipalities,
      flatColumns,
      getCellValue,
      getRowTotal,

      // Actions
      applyFilters,
      clearFilters,
      exportData,
    };
  },
};
</script>

<style lang="scss" scoped>
.damages-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

/* ── Header ─────────────────────────────────────────── */
.damages-header {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 0.5rem 1.5rem;
  align-items: center;
  padding: 1rem;
  flex-shrink: 0;
  background-color: #fff;
}

.damages-title {
  font-weight: 700;
  white-space: nowrap;
}

.damages-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.city-selector {
  min-width: 160px;
  max-width: 220px;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 0 1.5rem;
  align-items: start;
}

/* ── Table wrapper (scrollable) ─────────────────────── */
.damages-table-wrapper {
  flex: 1;
  overflow: auto;
  min-height: 0;
  background-color: #fbfbfb;
}

/* ── Table ──────────────────────────────────────────── */
.damages-table {
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 0.875rem;
}

/* Sticky first column */
.sticky-col {
  position: sticky;
  left: 0;
  z-index: 2;
  background-color: #fff;
}

/* Header rows */
.group-header-row th,
.sub-header-row th {
  padding: 0.65rem 1rem;
  text-align: center;
  font-weight: 600;
  white-space: nowrap;
  border-bottom: 1px solid #e9ecef;
  background-color: #f3f2f7;
  position: sticky;
  top: 0;
  z-index: 3;
}

.group-header-row .sticky-col,
.sub-header-row .sticky-col {
  z-index: 4;
}

.group-header-row th {
  top: 0;
}

.sub-header-row th {
  top: 2.1rem;
  font-weight: 500;
  color: #6c757d;
  font-size: 0.8rem;
}

.row-label-header {
  min-width: 180px;
}

.total-header {
  font-weight: 700;
}

/* Data rows */
.data-row td {
  padding: 0.65rem 1rem;
  text-align: center;
  white-space: nowrap;
  border-bottom: 1px solid #f0f0f0;
}

.row-label {
  text-align: left !important;
  font-weight: 600;
  min-width: 180px;
}

.total-cell {
  font-weight: 700;
}

.data-row:hover td {
  background-color: #f8f9fa;
}

.data-row:hover .sticky-col {
  background-color: #f8f9fa;
}

/* ── Button filter ──────────── */
.buttonFilter {
  padding: 0.5rem 0.75rem;
}

.damages-container {
  .dark-layout & {
    .damages-header {
      background-color: var(--light);
    }
    .damages-table-wrapper {
      background-color: var(--light);
    }
    .sticky-col {
      background-color: #283046 !important;
    }

    .group-header-row th,
    .sub-header-row th {
      background-color: #283046;
      border-bottom-color: #3b4253;
      color: #b4b7bd;
    }

    .data-row td {
      border-bottom-color: #3b4253;
      color: #b4b7bd;
      background-color: var(--main-dark);
    }

    .data-row:hover td,
    .data-row:hover .sticky-col {
      background-color: #1e2d3d !important;
    }

    .row-label {
      color: #d0d2d6;
    }

    .filterTittle {
      color: white;
    }
  }
}
</style>
